import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Bookmark, Plus, X } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { PromptDialog } from '@/components/PromptDialog';

export function BookmarkMenu({
  open, setOpen,
  bookmarks, setBookmarks,
  reportId,
  crossFilter, setCrossFilter,
  slicerSelections, setSlicerSelections,
  globalFilterValues, setGlobalFilterValues,
  paramValues, setParamValues,
}) {
  const [promptOpen, setPromptOpen] = useState(false);

  const handleSaveCurrent = async (name) => {
    if (!name || !reportId) return;
    try {
      const bm = await api.createBookmark(reportId, {
        name,
        filter_state: crossFilter,
        slicer_state: slicerSelections,
        global_filter_state: globalFilterValues,
        param_values: paramValues,
      });
      setBookmarks(prev => [bm, ...prev]);
      toast.success('View saved');
    } catch (err) { toast.error(err.message); }
    setOpen(false);
  };

  const handleRestore = (bm) => {
    if (bm.slicer_state) setSlicerSelections(bm.slicer_state);
    if (bm.global_filter_state) setGlobalFilterValues(bm.global_filter_state);
    if (bm.param_values) setParamValues(bm.param_values);
    if (bm.filter_state) setCrossFilter(bm.filter_state);
    toast.success(`Restored: ${bm.name}`);
    setOpen(false);
  };

  const handleDelete = async (bookmarkId) => {
    try {
      await api.deleteBookmark(reportId, bookmarkId);
      setBookmarks(prev => prev.filter(b => b.bookmark_id !== bookmarkId));
    } catch {}
  };

  return (
    <div className="relative">
      <Button variant="ghost" size="icon" onClick={() => setOpen(!open)} className="w-6 h-6 text-muted-foreground" title="Saved Views" data-testid="bookmarks-btn">
        <Bookmark className={`w-3 h-3 ${bookmarks.length > 0 ? 'text-amber-400' : ''}`} />
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full right-0 mt-1 glass-card rounded-lg shadow-xl border border-border/50 py-1 w-56 z-50">
            <button
              onClick={() => { setOpen(false); setPromptOpen(true); }}
              className="w-full text-left px-3 py-1.5 text-xs text-primary hover:bg-primary/10 flex items-center gap-2"
              data-testid="bookmark-save-current-btn"
            >
              <Plus className="w-3 h-3" /> Save Current View
            </button>
            {bookmarks.length > 0 && <div className="border-t border-border/30 my-1" />}
            {bookmarks.map(bm => (
              <div key={bm.bookmark_id} className="flex items-center gap-1 px-3 py-1.5 hover:bg-muted/30 group">
                <button onClick={() => handleRestore(bm)} className="text-xs text-foreground flex-1 text-left truncate">{bm.name}</button>
                <button onClick={() => handleDelete(bm.bookmark_id)} className="text-muted-foreground/30 hover:text-destructive opacity-0 group-hover:opacity-100"><X className="w-2.5 h-2.5" /></button>
              </div>
            ))}
            {bookmarks.length === 0 && <p className="px-3 py-2 text-[9px] text-muted-foreground/70">No saved views yet</p>}
          </div>
        </>
      )}

      <PromptDialog
        open={promptOpen} onOpenChange={setPromptOpen}
        title="Save Current View"
        label="Bookmark name"
        placeholder="e.g. Q1 2026 — APAC view"
        defaultValue={`View ${bookmarks.length + 1}`}
        confirmLabel="Save View"
        onConfirm={handleSaveCurrent}
        testId="bookmark-prompt"
      />
    </div>
  );
}
